import React from 'react';
import styled from 'styled-components';
import { Box, IconButton } from '@material-ui/core';
import useResponsive from '../../../../utils/responsive';
import ComponentAddressDetail from './ComponentAddressDetail';
import { useLanguage } from '../../../../context/language/language';
import { useSite } from '../../../../context/siteContext';
import { formatPhoneNumber } from '../../../../utils/orderUtil';
import { getImageURL } from '../../../../utils/getImageURL';

const IconEdit = getImageURL('/icon/edit.svg');
const Home = getImageURL('/icon/home.svg');
const IconSelected = getImageURL('/icon/ic-selected-primary.svg');
const officeBag = getImageURL('/icon/office-bag.svg');
const Placelocation = getImageURL('/icon/placelocation.svg');

const CardSession = styled.div(({ theme, selected }) => ({
  background: selected ? theme.colors.green100 : theme.colors.white,
  minHeight: '80px',
  height: 'auto',
  border: selected ? '2px solid #00BCB4' : '1px solid #E0E0E0',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: 8,
  paddingTop: 5,
  paddingBottom: 5,
  ...theme.radius.m,
  ...theme.fonts.header6,
}));

const Icon = styled.img(({ w, h }) => ({
  width: w,
  height: h,
  fill: 'red',
}));

const BtnIcon = styled(IconButton)(({ marginRight, flexGrow }) => ({
  width: 24,
  height: 24,
  margin: 'auto',
  flexGrow,
  marginRight,
}));

const CardIcon = styled.div(() => ({
  margin: '-35px 10px 0 17px',
  cursor: 'pointer',
}));

const Content = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  flexWrap: 'wrap',
  cursor: 'pointer',
  flexGrow: 1,
  paddingRight: 10,
  overflow: 'hidden',
}));

const TitleAddressRow = styled.div(({ theme, selected }) => ({
  width: '100%',
  color: selected ? theme.colors.green600 : theme.colors.grey700,
  ...theme.fonts.header6,
}));

const RecipientAddressRow = styled.div(() => ({
  display: 'flex',
  width: '100%',
}));

const RecipientTitle = styled.div(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  alignSelf: theme.isTabletOrMobile ? 'baseline' : 'initial'
}));

const RecipientPhoneNumber = styled.div(({ theme }) => ({
  ...theme.fonts.textM,
  whiteSpace: 'nowrap',
  marginLeft: 8,
  color: theme.colors.grey550,
  alignSelf: 'baseline',
  ...theme.isTabletOrMobile && {
    ...theme.fonts.textS,
  }
}));

const DetailAddressRow = styled.div(({ theme, selected }) => ({
  color: theme.colors.grey550,
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  ...theme.fonts.textS,
}));

const LabelPlaceName = styled.span(({ theme }) => ({
  overflow: 'hidden',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  textOverflow: 'ellipsis',
  ...theme.fonts.header6,
}));

const DefaultSpan = styled.span(({ theme, selected }) => ({
  backgroundColor: selected ? theme.colors.white : theme.colors.green100,
  fontSize: 10,
  fontWeight: 500,
  padding: '2px 6px',
  borderRadius: 9,
  marginLeft: 21,
}));

const LabelAddress = styled.div(() => ({
  marginTop: 4,
  overflow: 'hidden',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  textOverflow: 'ellipsis',
}));

const LabelSpan = styled.span(({ theme, isDefault, selected }) => ({
  backgroundColor: isDefault && !selected ? theme.colors.green100 : isDefault && selected ? theme.colors.white : theme.colors.grey50,
  fontWeight: 700,
  padding: '0px 4px',
  marginRight: 4,
  color: theme.colors.grey700,
  borderRadius: 6,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 12,
  ...theme.isTabletOrMobile && {
    fontSize: 10,
    borderRadius: 9,
    padding: '2px 6px',
  }
}));

const ComponentCustomerAddress = ({
  addressType,
  addressName,
  recipientName,
  contactNo,
  isDefault,
  placeName,
  addressNumber,
  thSubDistrict,
  thDistrict,
  thProvince,
  myArea,
  myCity,
  myState,
  myDistrict,
  mySubDistrict,
  postalCode,
  country,
  onEdit,
  onSelect,
  selected,
}) => {
  const { isTabletOrMobile } = useResponsive();
  const word = useLanguage();
  const site = useSite();

  let addressNameText;
  if (!['Home', 'Office'].includes(addressName)) {
    addressNameText = addressName;
  } else {
    addressNameText = word(addressName === 'Home' ? 'Contact.AddressType.Home' : 'Contact.AddressType.Work');
  }

  if (isTabletOrMobile) {
    const MobileTitle = () => (
      <TitleAddressRow>
        <LabelPlaceName>
          {addressType === 'Home' ? addressType : placeName}
          <RecipientTitle>{recipientName}</RecipientTitle>
          <RecipientPhoneNumber>{formatPhoneNumber(contactNo, true)}</RecipientPhoneNumber>
        </LabelPlaceName>
      </TitleAddressRow>
    );

    const MobileDefault = () => (
      <LabelAddress>
        {isDefault && <LabelSpan isDefault={isDefault}>{word('SavedAddress.Default')}</LabelSpan>}
        {addressType && <LabelSpan>{addressNameText}</LabelSpan>}
      </LabelAddress>
    );

    return (
      <ComponentAddressDetail
        addressType={addressType}
        addressName={addressName}
        recipientName={recipientName}
        contactNo={contactNo}
        isDefault={isDefault}
        placeName={placeName}
        addressNumber={addressNumber}
        thSubDistrict={thDistrict}
        thDistrict={thDistrict}
        thProvince={thProvince}
        myArea={myArea}
        myCity={myCity}
        myState={myState}
        myDistrict={myDistrict}
        mySubDistrict={mySubDistrict}
        postalCode={postalCode}
        country={country}
        onEdit={onEdit}
        onSelect={onSelect}
        FirstIcon={(
          <>
            {addressName === 'Home' && (
              <Icon src={Home} w="20px !important" h="20px" />
            )}
            {addressName === 'Office' && (
              <Icon src={officeBag} w="20px !important" h="20px" />
            )}
            {addressName !== 'Office' && addressName !== 'Home' && (
              <Icon src={Placelocation} w="20px !important" h="20px" />
            )}
          </>
        )}
        LastIcon={(
          <BtnIcon onClick={onEdit}>
            <Icon src={IconEdit} w="18px !important" h={18} />
          </BtnIcon>
        )}
        Title={<MobileTitle />}
        MobileDefault={<MobileDefault />}
      />
    );
  }

  let addressText = `${addressNumber} ${thSubDistrict}, ${thDistrict} ${thProvince} ${postalCode}`;
  if (site.MySite) {
    addressText = `${addressNumber} ${mySubDistrict ?? ''}, ${myDistrict ?? ''} ${myCity} ${myState} ${postalCode}`;
  }
  return (
    <>
      <CardSession selected={selected}>
        <CardIcon id="icon-select-address" onClick={onSelect}>
          {addressName === 'Home' && (
            <Icon src={Home} w="16px !important" h="20px" />
          )}
          {addressName === 'Office' && (
            <Icon src={officeBag} w="16px !important" h={20} />
          )}
          {!['Home', 'Office'].includes(addressName) && (
            <Icon src={Placelocation} w="16px !important" h={20} />
          )}
        </CardIcon>
        <Content id="content-select-address" onClick={onSelect}>
          <TitleAddressRow>
            <LabelPlaceName>
              <RecipientTitle>{recipientName}</RecipientTitle>
              <RecipientPhoneNumber>{formatPhoneNumber(contactNo, true)}</RecipientPhoneNumber>
            </LabelPlaceName>
          </TitleAddressRow>
          <DetailAddressRow selected={selected}>
            {addressText}
          </DetailAddressRow>
          <div>
            {isDefault &&  <LabelSpan selected={selected} isDefault={isDefault}>{word('SavedAddress.Default')}</LabelSpan>}
            {addressType && <LabelSpan>{addressNameText}</LabelSpan>}
          </div>
        </Content>
        <BtnIcon
          id="edit-address-quick"
          data-cta-name-en="Edit"
          data-cta-name-th="แก้ไข"
          data-cta-type="Button"
          onClick={onEdit}
          marginRight={16}
        >
          <Icon src={IconEdit} w={16} h={16} />
        </BtnIcon>
      </CardSession>
    </>
  );
};

export default ComponentCustomerAddress;
